import { useEffect, useState } from 'react';
import { outboundAPI } from '../api/client';
import type { ReviewQueueItem } from '../types';

interface ReviewQueuePanelProps {
  compact?: boolean;
  onChanged?: () => void;
}

export function ReviewQueuePanel({ compact = false, onChanged }: ReviewQueuePanelProps) {
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function refresh() {
    try {
      setLoading(true);
      setError(null);
      const rows = await outboundAPI.listReview({ limit: 20 });
      setItems(rows);
    } catch (err) {
      console.error('Failed to load review queue:', err);
      setError('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function runAction(itemId: string, action: 'approve' | 'reject' | 'override') {
    try {
      setBusyId(itemId);
      if (action === 'approve') await outboundAPI.approveReview(itemId);
      if (action === 'reject') await outboundAPI.rejectReview(itemId);
      if (action === 'override') await outboundAPI.overrideSend(itemId);
      await refresh();
      onChanged?.();
    } catch (err) {
      console.error('Failed review action:', err);
      setError('Action failed. Try again.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="rounded-xl border border-edge bg-white p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ink" data-ph-no-mask>Review Queue</h3>
          <p className="text-xs text-ink-secondary" data-ph-no-mask>{items.length} item{items.length === 1 ? '' : 's'} pending</p>
        </div>
        <button
          onClick={() => void refresh()}
          className="text-xs px-2 py-1 rounded-md border border-edge bg-canvas hover:bg-canvas-secondary"
          data-ph-no-mask
        >
          Refresh
        </button>
      </div>

      {loading && <p className="text-sm text-ink-secondary" data-ph-no-mask>Loading review queue...</p>}
      {error && !loading && <p className="text-sm text-red-600" data-ph-no-mask>{error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-ink-tertiary" data-ph-no-mask>No items need review right now.</p>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="rounded-lg border border-edge/70 p-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-ink truncate">
                    {item.contact_name || `Contact #${item.contact_id}`}
                  </p>
                  <p className="text-[11px] text-ink-secondary capitalize">
                    {item.status.replace('_', ' ')} · {item.reason.replace('_', ' ')}
                  </p>
                </div>
                {item.status === 'pending_override' && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-warning-bg text-warning font-semibold">
                    Override
                  </span>
                )}
              </div>

              <p className={`mt-2 text-xs text-ink-secondary ${compact ? 'line-clamp-2' : ''}`}>{item.content}</p>

              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => void runAction(item.id, 'approve')}
                  disabled={busyId === item.id}
                  className="text-xs px-2 py-1 rounded-md bg-ink text-white disabled:opacity-50"
                  data-ph-no-mask
                >
                  Approve
                </button>
                <button
                  onClick={() => void runAction(item.id, 'reject')}
                  disabled={busyId === item.id}
                  className="text-xs px-2 py-1 rounded-md border border-edge bg-white disabled:opacity-50"
                  data-ph-no-mask
                >
                  Reject
                </button>
                {item.status === 'pending_override' && (
                  <button
                    onClick={() => void runAction(item.id, 'override')}
                    disabled={busyId === item.id}
                    className="text-xs px-2 py-1 rounded-md border border-warning text-warning bg-warning-bg disabled:opacity-50"
                    data-ph-no-mask
                  >
                    Send override
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default ReviewQueuePanel;
